/**
 * Monday-start calendar with browser-first scheduling. Solve prepares authorized bounded snapshots,
 * displays provisional local results, and submits scheduling-only proposals for server validation.
 * Batched committed records and memoized per-cell rendering keep progress updates off the timeline.
 * Session, mutation, lease and cancellation fences reject late work; server optimization is an
 * explicit fallback. Refresh carries missed activities forward through the same local protocol.
 * @module apps/web/src/components/calendar/WeekView
 */

import { activityIsDone } from '@/components/activities/ActivityDone';
import { memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useAfterPaint } from '@/hooks/useAfterPaint';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { AlertTriangle, CalendarClock, Check, ClipboardList, Loader2, Wand2 } from 'lucide-react';
import { addDays, format, startOfWeek } from 'date-fns';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type {
  GhostBlocksData,
  CalendarEventWire,
  CapacityAnalysis,
  ScheduleBlock,
  TaskUI,
} from '@fluxability/types';
import { numberToPriority, taskToUI } from '@fluxability/types';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { afterNextPaint } from '@/lib/afterPaint';
import { rolloverCalendarLocally } from '@/lib/calendarRollover';
import { captureCalendarReadFence } from '@/lib/browserGhostBlocks';
import { previewBrowserSchedule, warmBrowserScheduling } from '@/lib/browserScheduling';
import { terminalSolveStatus, waitForSolveCompletion } from '@/lib/solveCompletion';
import { getCachedCalendarEvents, setCachedCalendarEvents } from '@/lib/calendarCache';
import { cn } from '@/lib/utils';
import { useScheduleStore } from '@/stores/scheduleStore';
import type { CalendarEvent } from '@/stores/scheduleStore';
import { useRouteSchedule, useRouteTasks } from '@/stores/routeData';
import { useTasksStore, captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import {
  CalendarTimelineCell,
  EMPTY_TIMELINE_ENTRIES,
  type TimelineEntry,
} from './CalendarTimeline';
import { assignLanes, laneInterval, type LanePlacement } from './timelineLanes';
import {
  runBrowserScheduleFlow,
  getSchedulingSessionEpoch,
  type BrowserScheduleStatus,
} from '@/lib/browserScheduleFlow';
import { schedulingProposalFromPreview } from '@/lib/schedulingProtocol';
import { CalendarCommitmentEditor } from './CalendarCommitmentEditor';
import { CalendarEmptyState } from './CalendarEmptyState';
import { CalendarChrome, CalendarCaption } from './CalendarChrome';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Detached, useDetachedState } from '@/hooks/useDetachedState';

/** Solver status shown while the background calendar sync runs. */
const SYNCING_STATUS = 'Syncing calendar constraints in the background...';

/** Local-clock hour labels for the 24-row day grid; each visible date supplies its own calendar context. */
const HOURS = Array.from({ length: 24 }, (_, index) => index);

/** Format calendar-cell identity from local year/month/day fields so UTC conversion cannot shift the displayed date. */
function toLocalDateString(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

/** Find Monday at the start of the local week used by schedule range requests. */
function asWeekStart(date: Date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/** Choose this view’s base hour-cell height from the user’s density preference; gesture zoom applies separately. */
function densitySlotHeight(density: 'compact' | 'balanced' | 'spacious') {
  if (density === 'compact') return 48;
  if (density === 'spacious') return 78;
  return 60;
}

/** Translate the dragged block’s center within the target hour into a clamped minute, using the supplied fallback when drag geometry is unavailable. Server scheduling still validates the proposed time. */
function getDropMinute(event: DragEndEvent, fallbackMinute: number, slotHeight: number) {
  const translated = event.active.rect.current.translated;
  const initial = event.active.rect.current.initial;
  const activeRect = translated || initial;
  const overRect = event.over?.rect;
  if (!activeRect || !overRect) return fallbackMinute;

  const pointerY = activeRect.top + activeRect.height / 2;
  const relative = Math.max(0, Math.min(slotHeight, pointerY - overRect.top));
  return Math.max(0, Math.min(59, Math.round((relative / slotHeight) * 60)));
}

/** Own calendar navigation, local provisional placements, cancellable browser solves and explicit server fallback. Canonical task records are updated only after authenticated server acceptance. */
export function WeekView() {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const consumedIntent = useRef<string | null>(null);
  const activeJob = useRef<string | undefined>(undefined);
  const [canUseServerFallback, setCanUseServerFallback] = useState(false);
  const [depth, setDepth] = useState(true);
  const timelineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (timelineRef.current) timelineRef.current.scrollTop = 8 * 60;
  }, []);
  const [isScheduling, setIsScheduling] = useState(false);
  const [previewTasks, setPreviewTasks] = useState<TaskUI[]>([]);
  const previewAbortRef = useRef<AbortController | null>(null);
  const userId = useUserStore(s => s.userId);
  useEffect(
    () =>
      useTasksStore.subscribe((state, previous) => {
        if (state.revision !== previous.revision)
          setPreviewTasks(current => (current.length ? [] : current));
      }),
    []
  );
  useEffect(() => {
    warmBrowserScheduling();
    setPreviewTasks(current => (current.length ? [] : current));
    return () => {
      const solving = Boolean(previewAbortRef.current && !previewAbortRef.current.signal.aborted);
      previewAbortRef.current?.abort();
      const jobId = activeJob.current;
      activeJob.current = undefined;
      if (jobId) void api.schedule.cancelAutoSchedule(jobId);
      // A kept-alive Week keeps its state while hidden; say why the solve stopped, so returning
      // does not show a stale "Preparing…" status.
      if (solving)
        setSolverStatus('Scheduling stopped when you left this screen. Any already saved placements remain.');
    };
  }, [userId]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);
  const [capacity, setCapacity] = useState<CapacityAnalysis | null>(null);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [constraintOverride, setConstraintOverride] = useState(false);
  const [slotHeight, setSlotHeight] = useState(60);
  // A zoom step resizes the grid rows at once (one style on the grid, not one per cell); blocks switch between
  // compact and full layouts in the render after that frame's paint, because re-laying out every
  // block costs tens of milliseconds and would hold the zoom itself back.
  const blockSlotHeight = useAfterPaint(slotHeight);
  const [solverStatus, setSolverStatus] = useState('Ready to solve open activities.');
  // Each editor owns the record it shows, so opening one re-renders that editor, not the grid.
  const commitmentEditor = useDetachedState<CalendarEvent | null>();
  const setEditingCalendarEvent = commitmentEditor.set;
  const taskEditor = useDetachedState<TaskUI | null>();
  const setEditingTask = taskEditor.set;
  const autoSyncedWeeksRef = useRef<Set<string>>(new Set());
  const isAutoSyncingRef = useRef(false);

  const currentDate = useScheduleStore(s => s.currentDate);
  const setCurrentDate = useScheduleStore(s => s.setCurrentDate);
  const setView = useScheduleStore(s => s.setView);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  // Read through route context so a hidden (kept-alive) Week stays current in the background.
  const { blocks, calendarEvents, ghostBlocks } = useRouteSchedule();
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);

  const tasks = useRouteTasks();
  const setTasks = useTasksStore(s => s.setTasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTask = useTasksStore(s => s.deleteTask);
  const workHours = useUserStore(s => s.settings.workHours);
  const calendarDensity = useUserStore(s => s.settings.calendarDensity);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 10 },
    })
  );

  const weekStart = useMemo(() => asWeekStart(currentDate), [currentDate]);
  const weekStartKey = toLocalDateString(weekStart);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );
  const workingWindow = useMemo(() => parseWorkingWindow(workHours), [workHours]);

  useEffect(() => {
    setView('week');
  }, [setView]);

  useEffect(() => {
    setSlotHeight(densitySlotHeight(calendarDensity));
  }, [calendarDensity]);

  // refreshScheduler reads the latest view state; only a week change or a new workspace loader
  // starts the load below.
  const refreshSchedulerForWeek = useEffectEvent(() => refreshScheduler());

  useEffect(() => {
    let cancelled = false;

    async function loadWeek() {
      void loadWorkspace();
      await refreshSchedulerForWeek();
      const connected = await refreshCalendarStatus();

      if (
        cancelled ||
        !connected ||
        autoSyncedWeeksRef.current.has(weekStartKey) ||
        isAutoSyncingRef.current
      ) {
        return;
      }

      isAutoSyncingRef.current = true;
      setIsSyncingCalendar(true);
      setSolverStatus(SYNCING_STATUS);

      try {
        const response = await api.calendar.sync();
        if (!cancelled && response.success) {
          await refreshSchedulerForWeek();
          autoSyncedWeeksRef.current.add(weekStartKey);
          const warningCount = Array.isArray(response.data?.warnings)
            ? response.data.warnings.length
            : 0;
          setSolverStatus(
            warningCount > 0
              ? `Calendar constraints partially synced. ${warningCount} calendar${warningCount === 1 ? '' : 's'} need attention.`
              : 'Calendar constraints are current. Ready to solve open activities.'
          );
        } else if (!response.success) {
          if (!cancelled) {
            setSolverStatus(
              `Calendar sync needs attention: ${response.error?.message || 'Reload calendars or reconnect Google Calendar.'}`
            );
          }
          console.warn('Background calendar sync failed', response.error);
        }
      } catch (error) {
        if (!cancelled) {
          setSolverStatus(
            'Calendar sync needs attention. Reload calendars or reconnect Google Calendar.'
          );
        }
        console.warn('Background calendar sync failed', error);
      } finally {
        isAutoSyncingRef.current = false;
        // Only one sync runs at a time (the ref above), so this one always ends the indicator,
        // even when a keep-alive hide cancelled the load: the hidden Week keeps its state.
        setIsSyncingCalendar(false);
        if (cancelled)
          setSolverStatus(current =>
            current === SYNCING_STATUS ? 'Ready to solve open activities.' : current
          );
      }
    }

    // Refresh after the screen has painted, so a mount or a keep-alive reveal shows at once.
    const cancelLoad = afterNextPaint(() => void loadWeek());

    return () => {
      cancelLoad();
      cancelled = true;
    };
  }, [weekStartKey, loadWorkspace]);

  const scheduledTaskBlocks = useMemo<ScheduleBlock[]>(() => {
    const recorded = new Set(blocks.filter(block => block.sessionId).map(block => block.taskId));
    return tasks
      .filter(task => task.scheduledStart && task.scheduledEnd && !(activityIsDone(task) && recorded.has(task.id)))
      .map(task => ({
        id: `task-${task.id}`,
        taskId: task.id,
        title: task.title,
        startTime: task.scheduledStart!,
        endTime: task.scheduledEnd!,
        isGhost: false,
        priority: task.priority,
        requiredEnergy: task.energyLevel,
        stateProfile: task.stateProfile,
        customAttributes: task.customAttributes,
      }));
  }, [tasks, blocks]);

  const calendarBlocks = useMemo(() => {
    return calendarEvents.map(event => ({
      id: `gcal-${event.id}`,
      taskId: event.id,
      title: event.title,
      startTime: event.start,
      endTime: event.end,
      isGhost: false,
      isCalendarEvent: true,
      isFreeTimeHold: event.isFlexHold,
      allowsConflict: event.allowConflict,
      priority: 4,
      stateProfile: event.stateProfile,
      customAttributes: event.customAttributes,
      requiredEnergy: event.stateProfile?.required_productivity_energy ?? undefined,
    }));
  }, [calendarEvents]);

  const displayBlocks = useMemo(() => {
    const merged = new Map<string, ScheduleBlock & { isCalendarEvent?: boolean }>();
    const taskIds = new Set(tasks.map(task => task.id));
    [...blocks, ...scheduledTaskBlocks, ...calendarBlocks].forEach(block => {
      if (block.isGhost && block.taskId && !taskIds.has(block.taskId)) return;
      merged.set(block.id, block);
    });
    for (const task of previewTasks) {
      if (!taskIds.has(task.id) || !task.scheduledStart || !task.scheduledEnd) continue;
      merged.set(`task-${task.id}`, {
        id: `task-${task.id}`,
        taskId: task.id,
        title: task.title,
        startTime: task.scheduledStart,
        endTime: task.scheduledEnd,
        isGhost: true,
        priority: task.priority,
        stateProfile: task.stateProfile,
        customAttributes: { ...task.customAttributes, scheduling_preview: true },
      });
    }
    return Array.from(merged.values());
  }, [blocks, scheduledTaskBlocks, calendarBlocks, tasks, previewTasks]);

  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const calendarEventById = useMemo(
    () => new Map(calendarEvents.map(event => [event.id, event])),
    [calendarEvents]
  );

  const blocksBySlot = useMemo(() => {
    const grouped = new Map<string, TimelineEntry[]>();
    // Overlapping blocks (for example concurrent activities) share their day column in lanes.
    const byDay = new Map<string, ScheduleBlock[]>();
    for (const block of displayBlocks) {
      const day = toLocalDateString(new Date(block.startTime));
      const list = byDay.get(day);
      if (list) list.push(block);
      else byDay.set(day, [block]);
    }
    const placements = new Map<string, LanePlacement>();
    for (const dayBlocks of byDay.values())
      for (const [id, placement] of assignLanes(dayBlocks.map(laneInterval)))
        placements.set(id, placement);
    for (const block of displayBlocks) {
      const start = new Date(block.startTime);
      const key = `${toLocalDateString(start)}-${start.getHours()}`;
      const existing = grouped.get(key);
      const entry = { block, task: taskById.get(block.taskId), ...placements.get(block.id) };
      if (existing) existing.push(entry);
      else grouped.set(key, [entry]);
    }
    return grouped;
  }, [displayBlocks, taskById]);

  const unscheduled = useMemo(
    () =>
      tasks.filter(
        task =>
          !task.scheduledStart &&
          !task.scheduledEnd &&
          task.status !== 'done' &&
          task.status !== 'completed' &&
          task.status !== 'cancelled'
      ),
    [tasks]
  );
  const needsDetails = useMemo(
    () => unscheduled.filter(task => getMissingPlanningFields(task).length > 0),
    [unscheduled]
  );
  const scheduledHours = useMemo(
    () =>
      tasks.reduce((sum, task) => {
        if (!task.scheduledStart || !task.scheduledEnd) return sum;
        return (
          sum +
          (new Date(task.scheduledEnd).getTime() - new Date(task.scheduledStart).getTime()) /
            3600000
        );
      }, 0),
    [tasks]
  );
  const hasCalendarContent = displayBlocks.length > 0 || isRefreshing;
  const pinchHandlers = usePinchZoom({
    setValue: setSlotHeight,
    min: 36,
    max: 156,
  });

  async function refreshTasks(): Promise<TaskUI[]> {
    const snapshot = captureTaskSnapshot();
    const response = await api.tasks.list();
    if (response.success && response.data) {
      const tasksList = Array.isArray(response.data) ? response.data : response.data.tasks;
      if (tasksList) {
        const nextTasks = tasksList.map(taskToUI);
        if (setTasks(nextTasks, snapshot)) return nextTasks;
      }
    }
    return useTasksStore.getState().tasks;
  }

  async function refreshScheduler() {
    if (previewAbortRef.current && !previewAbortRef.current.signal.aborted) return;
    const currentCalendar = captureCalendarReadFence();
    setIsRefreshing(true);
    try {
      await rolloverCalendarLocally(weekStartKey).catch(error => {
        console.warn('Local rollover needs a retry', error);
      });
      if (!currentCalendar()) return;

      const calendarStart = weekStart.toISOString();
      const calendarEnd = addDays(weekStart, 7).toISOString();

      const cachedCalendarEvents = getCachedCalendarEvents(calendarStart, calendarEnd);
      if (cachedCalendarEvents) {
        setCalendarEvents(cachedCalendarEvents);
        setSolverStatus(current =>
          current === 'Ready to solve open activities.' ||
          current.startsWith('Calendar constraints')
            ? 'Showing cached Calendar constraints. Refreshing Google Calendar in the background...'
            : current
        );
      }

      const calendarPromise = api.calendar
        .getEvents(calendarStart, calendarEnd)
        .then(calendarResponse => {
          if (currentCalendar() && calendarResponse.success && calendarResponse.data) {
            const normalizedEvents = normalizeCalendarEvents(calendarResponse.data);
            setCalendarEvents(normalizedEvents);
            setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
            setSolverStatus(current =>
              current === 'Ready to solve open activities.' ||
              current.startsWith('Showing cached Calendar constraints.')
                ? 'Calendar constraints are current. Ready to solve open activities.'
                : current
            );
          }
        })
        .catch(error => {
          console.warn('Background Google Calendar refresh failed', error);
        });

      const snapshot = captureTaskSnapshot();
      const [tasksResponse, ghostResponse, capacityResponse, overrideResponse] = await Promise.all([
        api.tasks.list(),
        api.schedule.getGhostBlocks(weekStartKey),
        api.schedule.checkFeasibility(weekStartKey),
        api.schedule.getConstraintOverride(weekStartKey),
      ]);

      if (!currentCalendar() || !isTaskSnapshotCurrent(snapshot)) return;
      let freshTaskIds = new Set(useTasksStore.getState().tasks.map(task => task.id));
      if (tasksResponse.success && tasksResponse.data) {
        const tasksList = Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : tasksResponse.data.tasks;
        if (tasksList) {
          const nextTasks = tasksList.map(taskToUI);
          setTasks(nextTasks);
          freshTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
        }
      }

      if (ghostResponse.success && ghostResponse.data) {
        setBlocks(normalizeScheduleBlocks(ghostResponse.data, freshTaskIds));
        setGhostBlocks(normalizeGhostBlocks(ghostResponse.data, freshTaskIds));
        if (!cachedCalendarEvents && ghostResponse.data.gcal_events?.length) {
          const normalizedEvents = normalizeCalendarEvents(ghostResponse.data.gcal_events || []);
          setCalendarEvents(normalizedEvents);
          setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
        }
      }

      if (capacityResponse.success && capacityResponse.data) {
        setCapacity(capacityResponse.data);
      }

      if (overrideResponse.success && overrideResponse.data) {
        setConstraintOverride(Boolean(overrideResponse.data.constraint_override_enabled));
      }

      void calendarPromise;
    } finally {
      setIsRefreshing(false);
    }
  }

  async function refreshCalendarStatus(): Promise<boolean> {
    const response = await api.calendar.getStatus();
    if (response.success && response.data) {
      const connected = Boolean(response.data.connected);
      setCalendarConnected(connected);
      return connected;
    }
    setCalendarConnected(false);
    return false;
  }

  /** Execute local batches first; a server solve requires the separately labelled fallback action. */
  async function runAutoSchedule(execution: 'browser' | 'server' = 'browser') {
    if (previewAbortRef.current && !previewAbortRef.current.signal.aborted) return;
    const abort = new AbortController();
    previewAbortRef.current = abort;
    const owner = useUserStore.getState().userId;
    const token = localStorage.getItem('flexiplanner-session-token');
    const sessionEpoch = getSchedulingSessionEpoch();
    let taskSnapshot = captureTaskSnapshot();
    /** Verify private session ownership independently of revisions changed by committed realtime events. */
    const assertOwner = () => {
      abort.signal.throwIfAborted();
      if (
        sessionEpoch !== getSchedulingSessionEpoch() ||
        owner !== useUserStore.getState().userId ||
        token !== localStorage.getItem('flexiplanner-session-token')
      )
        throw new Error('Your scheduling session changed.');
    };
    setPreviewTasks([]);
    setCanUseServerFallback(false);
    setIsScheduling(true);
    setSolverStatus(
      execution === 'browser' ? 'Preparing your local schedule…' : 'Starting server fallback…'
    );
    try {
      let response = await api.schedule.autoSchedule(weekStartKey, execution, abort.signal);
      if (response.success && response.data?.purpose === 'rollover') {
        // Another tab (or a navigation intent during refresh) can own a missed-only job.
        // Wait without taking its lease or cancelling it, then request the user's full solve.
        assertOwner();
        setSolverStatus('Finishing missed activities before starting your full solve…');
        await waitForServerSchedule(response.data, abort.signal);
        assertOwner();
        response = await api.schedule.autoSchedule(weekStartKey, execution, abort.signal);
        if (response.data?.purpose === 'rollover')
          throw new Error(
            'Missed activities are still being replanned. Try Solve again when they finish.'
          );
      }
      if (response.data?.job_id) activeJob.current = response.data.job_id;
      assertOwner();
      if (!response.success || !response.data)
        throw new Error(response.error?.message || 'Could not prepare scheduling.');
      let status = response.data;
      activeJob.current = status.job_id;
      if (execution === 'browser' && status.browser) {
        // Capture after preparation so preceding committed hydration does not invalidate the lease.
        taskSnapshot = captureTaskSnapshot();
        status = await runBrowserScheduleFlow({
          initial: status,
          signal: abort.signal,
          assertCurrent: () => {
            assertOwner();
            if (!isTaskSnapshotCurrent(taskSnapshot))
              throw new Error(
                'Activities changed while solving. Solve again with the current calendar.'
              );
          },
          solve: previewBrowserSchedule,
          proposal: schedulingProposalFromPreview,
          preview: (result, computeMs) => {
            setPreviewTasks(result.changes.map(taskToUI));
            setSolverStatus(
              `Local schedule ready (${computeMs.toFixed(1)} ms). Checking and saving…`
            );
          },
          submit: async (proposal, signal) => {
            const reply = await api.schedule.proposeSchedule(proposal, signal);
            if (!reply.success || !reply.data)
              throw new Error(
                reply.error?.message || 'The proposal could not be saved. Solve again to retry.'
              );
            return reply.data;
          },
          accepted: accepted => {
            assertOwner();
            if (isTaskSnapshotCurrent(taskSnapshot) && accepted.changes?.length)
              useTasksStore.getState().upsertTasks(accepted.changes.map(taskToUI));
            taskSnapshot = captureTaskSnapshot();
            setSolverStatus(accepted.message || 'Saving local placements…');
          },
          clearPreview: () => setPreviewTasks(current => (current.length ? [] : current)),
        });
      } else if (!terminalSolveStatus(status.status)) {
        // Explicit server fallback, plus compatibility with a server still rolling out leases.
        // The latter may already have started a legacy job; never start a second one implicitly.
        status = await waitForServerSchedule(status, abort.signal);
      }
      assertOwner();
      if (!terminalSolveStatus(status.status))
        throw new Error(status.message || 'The solve did not finish.');
      const scheduled = status.scheduled ?? 0;
      const optimized = status.optimized ?? 0;
      const failed = status.failed ?? 0;
      if (status.status === 'cancelled' || status.status === 'failed')
        throw new Error(status.message || 'Scheduling stopped.');
      activeJob.current = undefined;
      setSolverStatus(
        scheduled > 0
          ? `Placed ${scheduled} activit${scheduled === 1 ? 'y' : 'ies'} and re-optimized ${optimized} routine block${optimized === 1 ? '' : 's'}${failed ? `; ${failed} still open.` : '.'}`
          : failed
            ? `${failed} activities still need more room.`
            : 'No open activities needed placement.'
      );
      toast({
        title: failed ? 'Schedule partially solved' : 'Schedule solved',
        description: `${scheduled} activities placed, ${failed} blocked.`,
      });
      // Reconcile once after the whole job. Do not call rollover or recompute scheduling on refresh.
      void refreshTasks();
    } catch (error) {
      if (abort.signal.aborted) return;
      if (
        activeJob.current &&
        owner === useUserStore.getState().userId &&
        token === localStorage.getItem('flexiplanner-session-token')
      )
        void api.schedule.cancelAutoSchedule(activeJob.current);
      activeJob.current = undefined;
      if (owner !== useUserStore.getState().userId || sessionEpoch !== getSchedulingSessionEpoch())
        return;
      const message = error instanceof Error ? error.message : 'Try solving again.';
      setSolverStatus(message);
      setCanUseServerFallback(execution === 'browser');
      toast({ title: 'Schedule not saved', description: message, variant: 'destructive' });
      void refreshTasks();
    } finally {
      abort.abort();
      if (previewAbortRef.current === abort) {
        setPreviewTasks([]);
        setIsScheduling(false);
      }
    }
  }

  /** Await only an already-started server job; abort and job-ID fences prevent stuck or superseded UI. */
  async function waitForServerSchedule(initial: BrowserScheduleStatus, signal: AbortSignal) {
    let status = initial;
    for (let attempt = 0; attempt < 180 && !terminalSolveStatus(status.status); attempt++) {
      signal.throwIfAborted();
      const realtime = await waitForSolveCompletion(initial.job_id, 2000, signal);
      signal.throwIfAborted();
      if (realtime) status = realtime;
      else {
        const reply = await api.schedule.autoScheduleStatus();
        signal.throwIfAborted();
        if (!reply.success || !reply.data)
          throw new Error(reply.error?.message || 'Could not read scheduling status.');
        if (reply.data.job_id !== initial.job_id)
          throw new Error(
            'This solve was superseded by another solve. Refresh to see the latest schedule.'
          );
        status = reply.data;
      }
      setSolverStatus(status.message || 'Server fallback is running…');
    }
    return status;
  }

  /** Stop local work immediately; server cancellation is guarded by the captured job identity. */
  function cancelSchedule() {
    previewAbortRef.current?.abort();
    const jobId = activeJob.current;
    activeJob.current = undefined;
    setPreviewTasks([]);
    setIsScheduling(false);
    setSolverStatus('Scheduling cancelled. Any already saved placements remain.');
    if (jobId) void api.schedule.cancelAutoSchedule(jobId);
  }

  // Consume each schedule intent once: clear it from history, then solve with the latest state.
  const consumeScheduleIntent = useEffectEvent(() => {
    navigate(location.pathname, { replace: true, state: null });
    void runAutoSchedule();
  });
  const scheduleIntent = location.state?.scheduleIntent;
  useEffect(() => {
    if (!scheduleIntent || consumedIntent.current === scheduleIntent) return;
    consumedIntent.current = scheduleIntent;
    consumeScheduleIntent();
  }, [scheduleIntent]);

  async function confirmGhostBlock(ghostBlockId: string) {
    const response = await api.schedule.confirmGhostBlock(ghostBlockId);
    if (!response.success) {
      toast({
        title: 'Suggestion expired',
        description: response.error?.message || 'Generate a fresh set of suggestions.',
        variant: 'destructive',
      });
      return;
    }
    await refreshTasks();
    await refreshScheduler();
    toast({
      title: 'Suggestion confirmed',
      description: 'The activity has been pinned to the timeline.',
    });
  }

  async function syncCalendar() {
    setIsSyncingCalendar(true);
    try {
      const response = await api.calendar.sync();
      if (!response.success) throw new Error(response.error?.message || 'Calendar sync failed');
      await refreshScheduler();
      const warningCount = Array.isArray(response.data?.warnings)
        ? response.data.warnings.length
        : 0;
      toast({
        title: warningCount > 0 ? 'Calendar partially synced' : 'Calendar synced',
        description:
          warningCount > 0
            ? `${response.data?.synced ?? 0} events refreshed. ${warningCount} calendar${warningCount === 1 ? '' : 's'} need attention.`
            : `${response.data?.synced ?? 0} events refreshed.`,
      });
    } catch (error) {
      toast({
        title: calendarConnected ? 'Calendar sync failed' : 'Calendar not connected',
        description:
          error instanceof Error ? error.message : 'Connect Google Calendar in settings.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncingCalendar(false);
    }
  }

  async function toggleOverride() {
    const next = !constraintOverride;
    setConstraintOverride(next);
    const response = await api.schedule.setConstraintOverride(weekStartKey, next);
    if (!response.success) {
      setConstraintOverride(!next);
      toast({
        title: 'Override not saved',
        description: response.error?.message || 'Try again.',
        variant: 'destructive',
      });
      return;
    }
    await refreshScheduler();
  }

  function handleCalendarEventSaved(updatedEvent: CalendarEvent) {
    setCalendarEvents(
      calendarEvents.map(event => (event.id === updatedEvent.id ? updatedEvent : event))
    );
    setEditingCalendarEvent(null);
    toast({
      title: 'Commitment updated',
      description: updatedEvent.allowConflict
        ? 'Saved. Activities may now be planned over this commitment.'
        : 'Energy and attribute metadata were saved for this calendar commitment.',
    });
    void refreshScheduler();
  }

  async function handleSaveTask(updates: Partial<TaskUI>, editingTask: TaskUI | null) {
    if (!editingTask) return;

    try {
      const response = await api.tasks.update(editingTask.id, {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        split_policy: updates.splitPolicy,
        custom_attributes: updates.customAttributes,
      });
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI(response.data?.task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (error) {
      console.error('Failed to save activity', error);
      updateTask(editingTask.id, updates);
    }
  }

  async function handleDeleteTask(editingTask: TaskUI | null) {
    if (!editingTask) return;

    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to delete activity', error);
    }
  }

  async function handleRescheduleTomorrow(editingTask: TaskUI | null) {
    if (!editingTask) return;

    const response = await api.schedule.scheduleTaskTomorrow(editingTask.id);
    if (!response.success) {
      toast({
        title: 'Could not move activity',
        description: response.error?.message || 'No optimal slot was found tomorrow.',
        variant: 'destructive',
      });
      return;
    }

    const taskDoc = response.data?.task;
    if (taskDoc) updateTask(editingTask.id, taskToUI(taskDoc));
    setEditingTask(null);
    await refreshTasks();
    await refreshScheduler();
    toast({
      title: 'Moved to tomorrow',
      description:
        response.data?.notification?.message || 'The scheduler picked the best available slot.',
    });
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.data.current?.type === 'ScheduledTask' && over.data.current?.type === 'TimeSlot') {
      const block = active.data.current.block as ScheduleBlock;
      if (block.isGhost || block.id.startsWith('gcal-')) return;

      const targetDateKey = over.data.current.dateKey;
      const targetHour = over.data.current.hour;
      const oldStart = new Date(block.startTime);
      const durationMs = new Date(block.endTime).getTime() - oldStart.getTime();
      const targetMinute = getDropMinute(event, oldStart.getMinutes(), slotHeight);
      const newStart = new Date(`${targetDateKey}T00:00:00`);
      newStart.setHours(targetHour, targetMinute, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);

      updateTask(block.taskId, {
        scheduledStart: newStart.toISOString(),
        scheduledEnd: newEnd.toISOString(),
      });

      const response = await api.tasks.update(block.taskId, {
        do_date: newStart.toISOString(),
        do_date_end: newEnd.toISOString(),
      });
      if (!response.success) {
        await refreshTasks();
        toast({
          title: 'Move failed',
          description: response.error?.message || 'The activity stayed in its previous slot.',
          variant: 'destructive',
        });
      } else {
        await refreshScheduler();
      }
    }
  };

  const selectRefs = useRef({ taskById, calendarEventById, confirmGhostBlock });
  selectRefs.current = { taskById, calendarEventById, confirmGhostBlock };
  const selectBlock = useCallback((block: ScheduleBlock) => {
    if (block.customAttributes?.scheduling_preview) return;
    const current = selectRefs.current;
    if (block.isGhost) {
      void current.confirmGhostBlock(block.id);
      return;
    }
    if (block.isCalendarEvent) {
      const event = current.calendarEventById.get(block.taskId);
      if (event) setEditingCalendarEvent(event);
    } else {
      const task = current.taskById.get(block.taskId);
      if (task) setEditingTask(task);
    }
  }, [setEditingCalendarEvent, setEditingTask]);

  return (
    <div className="activity-calendar" data-depth={depth}>
      <CalendarChrome
        view="Week"
        period={`${format(weekStart, 'MMM d')} – ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`}
        previous={() => setCurrentDate(addDays(currentDate, -7))}
        next={() => setCurrentDate(addDays(currentDate, 7))}
        today={() => setCurrentDate(new Date())}
        depth={depth}
        setDepth={setDepth}
        refreshing={isRefreshing}
        refresh={() => void refreshScheduler()}
        zoom={{
          value: Math.round((slotHeight / 60) * 100),
          out: () => setSlotHeight(value => Math.max(40, value - 12)),
          in: () => setSlotHeight(value => Math.min(156, value + 12)),
        }}
        actions={
          <>
            <Button
              onClick={() => void runAutoSchedule()}
              onPointerEnter={() => warmBrowserScheduling('interactive')}
              onFocus={() => warmBrowserScheduling('interactive')}
              disabled={isScheduling || isRefreshing}
            >
              {isScheduling && <Loader2 className="mr-2 animate-spin" />}
              {isScheduling ? 'Solving…' : 'Solve'}
            </Button>
            {isScheduling && (
              <Button variant="ghost" onClick={cancelSchedule}>
                Cancel solve
              </Button>
            )}
            {canUseServerFallback && (
              <Button variant="outline" onClick={() => void runAutoSchedule('server')}>
                Use server fallback
              </Button>
            )}
          </>
        }
        tools={
          <>
            <Button variant="ghost" onClick={syncCalendar} disabled={isSyncingCalendar}>
              {isSyncingCalendar ? (
                <Loader2 className="mr-2 animate-spin" />
              ) : (
                <CalendarClock className="mr-2" />
              )}
              Sync
            </Button>
            <Button
              variant="ghost"
              onClick={toggleOverride}
              aria-pressed={constraintOverride}
              title="Override scheduling constraints"
            >
              {constraintOverride ? <Check className="mr-2" /> : <AlertTriangle className="mr-2" />}
              Override
            </Button>
          </>
        }
      >
        <span>
          <strong>
            {tasks.filter(task => !['done', 'completed', 'cancelled'].includes(task.status)).length}
          </strong>{' '}
          open activities
        </span>
        <span>
          <strong>{scheduledHours.toFixed(1)}</strong> h planned
        </span>
        {ghostBlocks.length > 0 && <span>{ghostBlocks.length} suggestions</span>}
        {capacity?.is_feasible === false && (
          <span role="alert">
            {capacity.overflow_hours?.toFixed(1) ?? 'Some'} hours overflow.{' '}
            {capacity.recommendations?.[0] ?? 'Reduce load or extend availability.'}
          </span>
        )}
        <span role="status" className={cn('calendar-connection', calendarConnected && 'connected')}>
          Calendar {calendarConnected ? 'connected' : 'not connected'}
        </span>
      </CalendarChrome>

      {needsDetails.length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 shadow-sm dark:border-amber-500/30 dark:bg-amber-950/25">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-amber-700 dark:text-amber-200" />
              <div>
                <h2 className="text-sm font-semibold text-amber-950 dark:text-amber-50">
                  {needsDetails.length} incomplete activit{needsDetails.length === 1 ? 'y' : 'ies'}{' '}
                  held from Solve
                </h2>
                <p className="text-xs text-amber-800/80 dark:text-amber-100/75">
                  Fill required fields in Unscheduled before they enter the calendar.
                </p>
              </div>
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-slate-950 dark:text-amber-50 dark:hover:bg-amber-950/40"
            >
              <NavLink to="/unscheduled">Open Unscheduled</NavLink>
            </Button>
          </div>
        </section>
      )}

      {!hasCalendarContent && <CalendarEmptyState />}
      <CalendarCaption />
      <div
        ref={timelineRef}
        className="native-scroll temporal-grid calendar-timeline relative"
        {...pinchHandlers}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="calendar-week-canvas">
            <div className="calendar-week-grid calendar-grid-header sticky top-0 z-20 grid border-b">
              <div className="calendar-time-label" aria-label="Local time zone">
                {
                  Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
                    .formatToParts(new Date())
                    .find(part => part.type === 'timeZoneName')?.value
                }
              </div>
              {days.map(day => (
                <div
                  key={day.toISOString()}
                  className={cn(
                    'calendar-grid-day',
                    toLocalDateString(day) === toLocalDateString(new Date()) && 'today'
                  )}
                >
                  <span>{format(day, 'EEE')}</span>
                  <strong>{format(day, 'd')}</strong>
                </div>
              ))}
            </div>

            {/* Row height lives on the grid: a zoom step restyles one element, not every cell. */}
            <div className="calendar-week-grid grid" style={{ gridAutoRows: `${slotHeight}px` }}>
              {HOURS.map(hour => (
                <HourRow
                  key={hour}
                  hour={hour}
                  days={days}
                  blocksBySlot={blocksBySlot}
                  slotHeight={blockSlotHeight}
                  workingWindow={workingWindow}
                  onSelect={selectBlock}
                />
              ))}
            </div>
          </div>
        </DndContext>
      </div>

      <div className="calendar-solver-status" role="status">
        {isScheduling ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Wand2 className="h-4 w-4" />
        )}
        <span>{solverStatus}</span>
      </div>

      <Detached state={commitmentEditor} initial={null}>
        {editingCalendarEvent => (
          <CalendarCommitmentEditor
            event={editingCalendarEvent}
            open={Boolean(editingCalendarEvent)}
            onClose={() => setEditingCalendarEvent(null)}
            onSave={handleCalendarEventSaved}
          />
        )}
      </Detached>
      <Detached state={taskEditor} initial={null}>
        {editingTask => (
          <TaskEditor
            task={editingTask}
            open={Boolean(editingTask)}
            onClose={() => setEditingTask(null)}
            onSave={updates => handleSaveTask(updates, editingTask)}
            onDelete={() => handleDeleteTask(editingTask)}
            onRescheduleTomorrow={() => handleRescheduleTomorrow(editingTask)}
          />
        )}
      </Detached>
    </div>
  );
}

/** Keep each physical day column registered across week changes; dateKey carries the current drop date. Render one local-clock hour across the visible week using pre-indexed block, activity, and calendar-event maps. Clicking dispatches ghost confirmation or the correct editor; work-hour shading is presentation, not a server feasibility decision. */
const HourRow = memo(function HourRow({
  hour,
  days,
  blocksBySlot,
  slotHeight,
  workingWindow,
  onSelect,
}: {
  /** Local hour shared by the visible day columns. */
  hour: number;
  /** Dates in visual column order. */
  days: Date[];
  /** Indexed placements with activity records, avoiding whole-array scans in each cell. */
  blocksBySlot: Map<string, TimelineEntry[]>;
  /** Pixel height of one hour. */
  slotHeight: number;
  /** Visual work-window bounds; not a scheduling constraint. */
  workingWindow: WorkingWindow;
  /** Stable callback opening the appropriate editor or confirming a suggestion. */
  onSelect: (block: ScheduleBlock) => void;
}) {
  const label =
    hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
  const boundaryMinutes = getWorkingBoundaryMinutes(hour, workingWindow);
  return (
    <>
      <div
        className={cn(
          'calendar-time-label border-r border-t text-right',
          boundaryMinutes.length > 0 && 'border-t-foreground/80 border-t-2'
        )}
      >
        {label}
      </div>
      {days.map((day, column) => {
        const dateKey = toLocalDateString(day);
        return (
          <CalendarTimelineCell
            key={column}
            id={`week-column-${column}-hour-${hour}`}
            dateKey={dateKey}
            hour={hour}
            slotHeight={slotHeight}
            workStart={workingWindow.startMinutes}
            workEnd={workingWindow.endMinutes}
            entries={blocksBySlot.get(`${dateKey}-${hour}`) || EMPTY_TIMELINE_ENTRIES}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
});

/** Collect missing title, bundle, duration, and four core state scores before week-view planning. This local check does not inspect required custom attributes or replace server validation. */
function getMissingPlanningFields(task: TaskUI): string[] {
  const missing: string[] = [];
  if (!String(task.title || '').trim()) missing.push('title');
  if (!task.bundleId) missing.push('bundle');
  if (!Number.isFinite(Number(task.estimatedDuration)) || Number(task.estimatedDuration) <= 0) {
    missing.push('duration');
  }

  const profile = task.stateProfile;
  const requiredScores: Array<[keyof NonNullable<TaskUI['stateProfile']>, string]> = [
    ['required_productivity_energy', 'required P'],
    ['required_rejuvenation_energy', 'required RJ'],
    ['productivity_return', 'P return'],
    ['rejuvenation_return', 'RJ return'],
  ];
  for (const [key, label] of requiredScores) {
    const value = profile?.[key];
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      missing.push(label);
    }
  }

  return Array.from(new Set(missing));
}

/** Local clock bounds in minutes from midnight used only for work-hour shading and boundary marks. */
type WorkingWindow = {
  /** Local minutes after midnight at which the working interval starts. */
  startMinutes: number;
  /** Local minutes after midnight at which the working interval ends. */
  endMinutes: number;
};

/** Convert preferred start/end clock strings to minute bounds, defaulting absent or nonnumeric values to 09:00–17:00. */
function parseWorkingWindow(
  workHours?: {
    /** Local workday start clock string parsed into minutes after midnight. */ start?: string;
    /** Local workday end clock string parsed into minutes after midnight. */ end?: string;
  } | null
): WorkingWindow {
  return {
    startMinutes: parseClockMinutes(workHours?.start, 9 * 60),
    endMinutes: parseClockMinutes(workHours?.end, 17 * 60),
  };
}

/** Convert an hour/minute string to minutes from midnight and clamp to the day bounds. Missing or nonnumeric input uses the caller’s fallback. */
function parseClockMinutes(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const [hourText, minuteText = '0'] = value.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return fallback;
  return Math.max(0, Math.min(24 * 60, hour * 60 + minute));
}

/** Locate work-window boundaries inside this hour and express them as minute offsets for border positioning. */
function getWorkingBoundaryMinutes(hour: number, window: WorkingWindow): number[] {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return [window.startMinutes, window.endMinutes]
    .filter(minutes => minutes >= hourStart && minutes < hourEnd)
    .map(minutes => minutes - hourStart);
}

/** Project ghost proposals and recorded work into display blocks; pause intervals stay empty. A ghost remains tentative; a work segment represents recorded activity rather than a new scheduling request. */
function normalizeScheduleBlocks(
  data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>,
  taskIds?: Set<string>
) {
  const ghostBlocks = (data.ghost_blocks || [])
    .filter(block => !taskIds || taskIds.has(block.task_id))
    .map(block => ({
      id: block.id,
      taskId: block.task_id,
      title: block.title || 'Suggested activity',
      startTime: block.proposed_start,
      endTime: block.proposed_end,
      isGhost: true,
    }));

  const workSegments = (data.work_segments || []).filter(segment => segment.segment_type !== 'pause').map(segment => ({
    id: segment.id,
    sessionId: segment.session_id,
    workSegmentIndex: segment.segment_index,
    workSegmentCount: segment.segment_count,
    taskId: segment.task_id ?? '',
    title: segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: 3,
  }));

  return [...ghostBlocks, ...workSegments];
}

/** Project proposed start/end times and confidence/cost explanations into the tentative-block store without confirming the proposed placement. */
function normalizeGhostBlocks(
  data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>,
  taskIds?: Set<string>
) {
  return (data.ghost_blocks || [])
    .filter(block => !taskIds || taskIds.has(block.task_id))
    .map(block => ({
      id: block.id,
      taskId: block.task_id,
      proposedStart: block.proposed_start,
      proposedEnd: block.proposed_end,
      cost: block.cost,
      confidence: block.confidence,
      reason: block.reason,
    }));
}

/** Adapt calendar events into UI records. Accepts both wire spellings: camelCase from `/calendar/events` and snake_case commitments from schedule responses. Defaults commitments to immovable, parses custom attributes, and removes entries that should not appear as timed commitments. */
function normalizeCalendarEvents(events: CalendarEventWire[] = []) {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time || '',
      end: event.end || event.end_time || '',
      isImmovable: Boolean(event.isImmovable ?? event.is_immovable ?? true),
      isFlexHold: Boolean(event.isFlexHold ?? event.is_flex_hold),
      stateProfile: event.stateProfile || event.state_profile || null,
      customAttributes: parseCustomAttributes(event.customAttributes ?? event.custom_attributes),
      allowConflict: Boolean(event.allowConflict ?? event.allow_conflict),
      seriesAllowConflict: Boolean(event.seriesAllowConflict),
      isRecurring: Boolean(event.isRecurring),
    }))
    .filter(isRenderableCalendarEvent);
}

/** Exclude date-only/all-day entries and imported Canvas or Book-Tracker activities from timed commitment rendering. Protected Fluxability holds remain visible after the timed-event check, preventing duplicate imported work on the calendar. */
function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return (
    source !== 'canvas' &&
    source !== 'book-tracker' &&
    !attributes.canvas &&
    !attributes.book_tracker
  );
}

/** Treat either endpoint without a time separator as all-day/untimed. This is a display-shape check, not a full timestamp validator. */
function isAllDayCalendarEvent(event: {
  /** Optional event start text; absence of a time separator marks the event untimed. */ start?: string;
  /** Optional event end text; absence of a time separator marks the event untimed. */ end?: string;
}): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

/** Accept existing object values or JSON object strings for legacy custom attributes. Missing, malformed, or scalar values become an empty record; the function does not validate individual field meanings. */
function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
